import { useState, useRef } from 'react'
import type { ProyectoPlan } from './tipos'
import { extraerTextoDeArchivo, procesarTextoPresupuesto } from './procesadorPresupuesto'
import { MarcaMega } from '@/shared/marca/MarcaMega'
import { IconoArchivo, IconoCheck } from '@/shared/ui/Iconos/Iconos'
import estilos from './ModalImportarArchivo.module.css'

type PropsModal = {
  abierto: boolean
  onCerrar: () => void
  onConfirmar: (proyecto: ProyectoPlan) => void
}

function IconoEscudo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  )
}

function IconoCerrarSvg() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  )
}

export function ModalImportarArchivo({ abierto, onCerrar, onConfirmar }: PropsModal) {
  const [arrastrando, setArrastrando] = useState(false)
  const [procesando, setProcesando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [borrador, setBorrador] = useState<ProyectoPlan | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!abierto) return null

  async function manejarArchivo(archivo: File) {
    setError(null)
    setProcesando(true)
    try {
      const texto = await extraerTextoDeArchivo(archivo)
      const detectado = procesarTextoPresupuesto(texto, archivo.name)

      if (detectado.etapas.length === 0) {
        setError(
          'No se detectaron actividades de obra/instalación en el archivo. Verifica que contenga términos como Suministro, Instalación, Excavación o Certificación.'
        )
        setBorrador(null)
      } else {
        setBorrador(detectado)
      }
    } catch (err: any) {
      setError(err?.message || 'Error al procesar el archivo. Intenta con un archivo .pdf o .txt válido.')
      setBorrador(null)
    } finally {
      setProcesando(false)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setArrastrando(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      void manejarArchivo(e.dataTransfer.files[0])
    }
  }

  function saltarAEtapa(etapaId: string) {
    const el = document.getElementById(etapaId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  function handleGuardar() {
    if (!borrador) return
    onConfirmar(borrador)
    onCerrar()
  }

  const totalModulos = borrador
    ? borrador.etapas.reduce((acc, e) => acc + e.modulos.length, 0)
    : 0
  const totalHoras = borrador
    ? borrador.etapas.reduce(
        (acc, e) => acc + e.modulos.reduce((mAcc, m) => mAcc + m.horas, 0),
        0
      )
    : 0

  return (
    <div className={estilos.overlay} onClick={onCerrar}>
      <div className={estilos.modal} onClick={(e) => e.stopPropagation()}>
        {/* Cabecera */}
        <header className={estilos.header}>
          <div className={estilos.tituloRow}>
            <MarcaMega tamanoLogo={22} soloLogo={true} />
            <h2 className={estilos.titulo}>
              Importar Proyecto desde Presupuesto (PDF / TXT)
            </h2>
          </div>
          <button
            type="button"
            className={estilos.btnCerrar}
            onClick={onCerrar}
            title="Cerrar modal"
          >
            <IconoCerrarSvg />
          </button>
        </header>

        {/* Cuerpo Desplazable con Slider Vertical */}
        <div className={estilos.cuerpo}>
          {!borrador && !procesando && (
            <>
              <div
                className={`${estilos.dropzone} ${
                  arrastrando ? estilos.dropzoneActivo : ''
                }`}
                onDragOver={(e) => {
                  e.preventDefault()
                  setArrastrando(true)
                }}
                onDragLeave={() => setArrastrando(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.txt"
                  className={estilos.inputOculto}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      void manejarArchivo(e.target.files[0])
                    }
                  }}
                />
                <IconoArchivo className={estilos.iconoDropzoneSvg} />
                <span className={estilos.dropzoneTextoPrincipal}>
                  Arrastra aquí tu presupuesto PDF o TXT, o haz clic para examinar
                </span>
                <span className={estilos.dropzoneTextoSecundario}>
                  Formatos admitidos: .pdf, .txt (Procesamiento local en memoria)
                </span>
              </div>

              <div className={estilos.avisoSeguridad}>
                <IconoEscudo className={estilos.iconoEscudoSvg} />
                <div>
                  <b>Privacidad y Ejecución Local Garantizada:</b> El archivo se procesa{' '}
                  <b>100% en la memoria de tu navegador</b>. Ningún archivo se guarda en
                  disco ni en la base de datos de producción. El analizador separa
                  automáticamente por etapas y filtra actividades ejecutables
                  (instalación, suministro, obra civil, certificación) descartando materiales sueltos.
                </div>
              </div>

              {error && (
                <div style={{ color: '#ff6b6b', fontSize: '0.85rem' }}>
                  {error}
                </div>
              )}
            </>
          )}

          {procesando && (
            <div className={estilos.cargandoSpinner}>
              <svg className={estilos.spinnerSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
              <span>Analizando presupuesto y separando actividades por etapas...</span>
            </div>
          )}

          {borrador && !procesando && (
            <>
              {/* Tarjeta de Datos del Proyecto */}
              <div className={estilos.borradorCabecera}>
                <div className={estilos.borradorGrid}>
                  <div className={estilos.campo}>
                    <label>Nombre del Proyecto</label>
                    <input
                      type="text"
                      value={borrador.nombre}
                      onChange={(e) =>
                        setBorrador({ ...borrador, nombre: e.target.value })
                      }
                    />
                  </div>

                  <div className={estilos.campo}>
                    <label>Cliente / Solicitante</label>
                    <input
                      type="text"
                      value={borrador.cliente || ''}
                      placeholder="Nombre del cliente..."
                      onChange={(e) =>
                        setBorrador({ ...borrador, cliente: e.target.value })
                      }
                    />
                  </div>

                  <div className={estilos.campo}>
                    <label>Ubicación / Sitio</label>
                    <input
                      type="text"
                      value={borrador.ubicacion || ''}
                      placeholder="Ciudad o sitio de la obra..."
                      onChange={(e) =>
                        setBorrador({ ...borrador, ubicacion: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className={estilos.metricasResumen}>
                  <span className={estilos.metricaChip}>
                    {borrador.etapas.length} etapas separadas
                  </span>
                  <span className={estilos.metricaChip}>
                    {totalModulos} actividades de instalación filtradas
                  </span>
                  <span className={estilos.metricaChip}>
                    ~{totalHoras} horas de trabajo estimadas
                  </span>
                </div>
              </div>

              {/* Barra de Salto Rápido a Etapa */}
              <div className={estilos.navEtapasRapida}>
                {borrador.etapas.map((etapa) => (
                  <button
                    key={etapa.id}
                    type="button"
                    className={estilos.chipSaltoEtapa}
                    onClick={() => saltarAEtapa(etapa.id)}
                    title={etapa.nombre}
                  >
                    Etapa {etapa.orden}
                  </button>
                ))}
              </div>

              {/* Título de la sección */}
              <div className={estilos.seccionEtapasTitulo}>
                <span>Etapas y Actividades Detectadas en el Borrador</span>
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ffc43f',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    textDecoration: 'underline',
                  }}
                  onClick={() => setBorrador(null)}
                >
                  Cargar otro archivo
                </button>
              </div>

              {/* Bloques de Cada Etapa Separada Visualmente */}
              <div className={estilos.contenedorEtapas}>
                {borrador.etapas.map((etapa) => {
                  const horasEtapa = etapa.modulos.reduce((acc, m) => acc + m.horas, 0)
                  return (
                    <article
                      key={etapa.id}
                      id={etapa.id}
                      className={estilos.bloqueEtapa}
                    >
                      <header className={estilos.bloqueEtapaHeader}>
                        <div className={estilos.etapaTituloRow}>
                          <span className={estilos.etapaPill}>Etapa {etapa.orden}</span>
                          <span className={estilos.etapaNombreTexto}>{etapa.nombre}</span>
                        </div>
                        <span className={estilos.etapaStatsBadge}>
                          {etapa.modulos.length} actividades · {horasEtapa}h
                        </span>
                      </header>

                      <div className={estilos.tablaModulos}>
                        {etapa.modulos.map((m) => (
                          <div key={m.id} className={estilos.filaModulo}>
                            <span className={estilos.modCodigoBadge}>{m.codigo}</span>
                            <span className={estilos.modNombreTexto}>{m.nombre}</span>
                            <span className={estilos.modUnidadBadge}>
                              {m.cantidad} {m.unidad}
                            </span>
                            <span className={estilos.modHorasBadge}>{m.horas}h</span>
                          </div>
                        ))}
                      </div>
                    </article>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Pie del Modal */}
        <footer className={estilos.footer}>
          <button
            type="button"
            className={estilos.btnCancelar}
            onClick={onCerrar}
          >
            {borrador ? 'Descartar' : 'Cancelar'}
          </button>

          {borrador && (
            <button
              type="button"
              className={estilos.btnConfirmar}
              onClick={handleGuardar}
            >
              <IconoCheck />
              <span>Confirmar e Importar al Plan</span>
            </button>
          )}
        </footer>
      </div>
    </div>
  )
}
